import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import Logo from "../../assets/IBM-Q2D-Logo.png";

/* -------------------------------------------------------
 * STYLES
 * ----------------------------------------------------- */
const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 10,
    fontFamily: "Helvetica",
  },

  /* HEADER */
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    paddingBottom: 10,
    marginBottom: 12,
  },

  logoRow: {
    flexDirection: "row",
    alignItems: "center",
  },

  logo: {
    width: 60,
    height: 60,
    marginRight: 10,
  },

  orgText: {
    fontSize: 9,
    color: "#444",
  },

  gstInfo: {
    fontSize: 8,
    marginTop: 2,
  },

  rightHeader: {
    textAlign: "right",
    justifyContent: "center",
  },

  /* TITLE */
  title: {
    fontSize: 14,
    fontWeight: "bold",
    textAlign: "center",
    marginVertical: 14,
    textTransform: "uppercase",
  },

  /* META */
  metaSection: {
    marginBottom: 14,
  },

  metaRow: {
    flexDirection: "row",
    marginBottom: 4,
  },

  label: {
    width: "40%",
    fontWeight: "bold",
  },

  value: {
    width: "60%",
  },

  /* TABLE */
  table: {
    borderWidth: 1,
    borderColor: "#999",
    marginTop: 8,
  },

  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#999",
  },

  th: {
    fontWeight: "bold",
    textAlign: "center",
    padding: 5,
    borderRightWidth: 1,
    borderRightColor: "#999",
  },

  tdSl: {
    width: "10%",
    textAlign: "center",
    padding: 5,
    borderRightWidth: 1,
    borderRightColor: "#999",
  },

  tdDesc: {
    width: "70%",
    padding: 5,
    borderRightWidth: 1,
    borderRightColor: "#999",
  },

  tdAmt: {
    width: "20%",
    textAlign: "right",
    padding: 5,
  },

  totalRow: {
    backgroundColor: "#f2f2f2",
    fontWeight: "bold",
  },

  /* AMOUNT WORDS */
  amountWordsBox: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: "#999",
    padding: 6,
    fontStyle: "italic",
  },

  /* FOOTER */
  footer: {
    marginTop: 20,
    textAlign: "center",
    fontSize: 8,
    color: "#666",
  },
});

/* -------------------------------------------------------
 * HELPERS
 * ----------------------------------------------------- */
const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/* -------------------------------------------------------
 * NUMBER → WORDS (INDIAN)
 * ----------------------------------------------------- */
const numberToWords = (num) => {
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
  const teens = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  const twoDigits = (n) => {
    if (n < 10) return ones[n];
    if (n < 20) return teens[n - 10];
    return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
  };

  const threeDigits = (n) => {
    let str = "";
    if (n >= 100) {
      str += ones[Math.floor(n / 100)] + " Hundred ";
      n %= 100;
    }
    if (n > 0) str += twoDigits(n);
    return str.trim();
  };

  if (num === 0) return "Zero Rupees Only";

  let result = "";
  if (num >= 100000) {
    result += twoDigits(Math.floor(num / 100000)) + " Lakh ";
    num %= 100000;
  }
  if (num >= 1000) {
    result += twoDigits(Math.floor(num / 1000)) + " Thousand ";
    num %= 1000;
  }
  if (num > 0) result += threeDigits(num);

  return result.trim() + " Rupees Only";
};

/* -------------------------------------------------------
 * GST CALCULATION (CGST + SGST ONLY)
 * ----------------------------------------------------- */
const GST_RATE = 0.18;

const calculateGST = (total) => {
  const taxable = total / (1 + GST_RATE);
  const gst = total - taxable;

  return {
    taxable: taxable.toFixed(2),
    cgst: (gst / 2).toFixed(2),
    sgst: (gst / 2).toFixed(2),
  };
};

/* -------------------------------------------------------
 * COMPONENT
 * ----------------------------------------------------- */
const ReceiptPDF = ({ payment = {} }) => {
  const totalAmount = Number(payment.amount || 0);
  const { taxable, cgst, sgst } = calculateGST(totalAmount);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* HEADER */}
        <View style={styles.header}>
          <View style={styles.logoRow}>
            <Image src={Logo} style={styles.logo} />
            <View>
              <Text style={styles.orgText}>Sattva Global City, Mysore Road</Text>
              <Text style={styles.orgText}>Rajarajeshwari Nagar, Bengaluru – 560059</Text>
              <Text style={styles.gstInfo}>GSTIN: 32AABCQ1286G1ZO</Text>
            </View>
          </View>

          <View style={styles.rightHeader}>
            <Text>Date: {formatDate(payment.paidAt)}</Text>
          </View>
        </View>

        {/* TITLE */}
        <Text style={styles.title}>Application Fee Receipt</Text>

        {/* META */}
        <View style={styles.metaSection}>
          <View style={styles.metaRow}>
            <Text style={styles.label}>Receipt Number</Text>
            <Text style={styles.value}>{payment.applicationNo || "-"}</Text>
          </View>

          <View style={styles.metaRow}>
            <Text style={styles.label}>Candidate Name</Text>
            <Text style={styles.value}>{payment.fullName || "-"}</Text>
          </View>

          <View style={styles.metaRow}>
            <Text style={styles.label}>Payment Mode</Text>
            <Text style={styles.value}>Online Payment</Text>
          </View>

          <View style={styles.metaRow}>
            <Text style={styles.label}>Transaction ID</Text>
            <Text style={styles.value}>{payment.orderId || "-"}</Text>
          </View>
        </View>

        {/* TABLE */}
        <View style={styles.table}>
          <View style={styles.tableRow}>
            <Text style={[styles.tdSl, styles.th]}>S.No</Text>
            <Text style={[styles.tdDesc, styles.th]}>Description</Text>
            <Text style={[styles.tdAmt, styles.th]}>Amount (INR)</Text>
          </View>

          <View style={styles.tableRow}>
            <Text style={styles.tdSl}>1</Text>
            <Text style={styles.tdDesc}>Application Fee (INR)</Text>
            <Text style={styles.tdAmt}>{taxable}</Text>
          </View>

          <View style={styles.tableRow}>
            <Text style={styles.tdSl}></Text>
            <Text style={styles.tdDesc}>+ CGST @ 9% (INR)</Text>
            <Text style={styles.tdAmt}>{cgst}</Text>
          </View>

          <View style={styles.tableRow}>
            <Text style={styles.tdSl}></Text>
            <Text style={styles.tdDesc}>+ SGST @ 9% (INR)</Text>
            <Text style={styles.tdAmt}>{sgst}</Text>
          </View>

          <View style={[styles.tableRow, styles.totalRow]}>
            <Text style={styles.tdSl}></Text>
            <Text style={styles.tdDesc}>Total (INR)</Text>
            <Text style={styles.tdAmt}>{totalAmount.toFixed(2)}</Text>
          </View>
        </View>

        {/* AMOUNT IN WORDS */}
        <View style={styles.amountWordsBox}>
          <Text>
            Amount in Words: {numberToWords(Math.round(totalAmount))}
          </Text>
        </View>

        {/* FOOTER */}
        <View style={styles.footer}>
          <Text>
            This is a computer generated receipt and does not require a physical signature.
          </Text>
          <Text>Website: www.q2d.in | Email: info@q2d.in</Text>
        </View>
      </Page>
    </Document>
  );
};

export default ReceiptPDF;
